import React, { useRef, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { handleSpringDataMutationRequest } from "../../utils/apiUtils";
import { toast } from "react-toastify";

export default function Branding({ emailId }) {
  const [saveLoader, setSaveLoader] = useState(false);
  const [hostUrl, setHostUrl] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [faviconPreview, setFaviconPreview] = useState("");
  const [newLogo, setNewLogo] = useState(null);
  const [newFavicon, setNewFavicon] = useState(null);

  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  const handleFileChange = (event, kind) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result.split(",")[1]; 
      const fileType = file.type.split("/")[1]; 
      if (kind === "logo") {
        setNewLogo({ data: base64String, type: fileType });
        setLogoPreview(reader.result);
      } else {
        setNewFavicon({ data: base64String, type: fileType });
        setFaviconPreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleClear = (kind) => {
    if (kind === "logo") {
      setNewLogo(null);
      setLogoPreview("");
      if (logoInputRef.current) logoInputRef.current.value = "";
    } else {
      setNewFavicon(null);
      setFaviconPreview("");
      if (faviconInputRef.current) faviconInputRef.current.value = "";
    }
  };

  const handleSave = () => {
    
    if (!hostUrl.trim()) {
      toast.error("Please enter a Host URL");
      return;
    }

    setSaveLoader(true);
    const payload = {
      hostUrl: hostUrl.trim(),
      groupEmail: emailId,
      status: "ENABLED",
      ...(newLogo && { logoData: newLogo.data, logoType: newLogo.type }),
      ...(newFavicon && {
        faviconData: newFavicon.data,
        faviconType: newFavicon.type,
      }),
    };
    handleSpringDataMutationRequest(
      "POST",
      `common/set-group-whitelabel-config`,
      payload,
      false,
      false
    )
      .then((res) => {
        if (res?.code && res.code !== "200") {
          toast.error(res?.message || "Failed to update branding");
        } else {
          toast.success("Branding updated successfully");
        }
      })
      .catch(() => toast.error("Something went wrong"))
      .finally(() => setSaveLoader(false));
  };

  return (
    <Box>
      <Card sx={{ marginBottom: 2 }}>
        <CardHeader title={"Whitelabel configuration"} />
        <Divider />
        <CardContent>
          <Stack spacing={4}>
            <TextField
              size="small"
              label="Host URL"
              placeholder="e.g. pms.yourdomain.com"
              variant="outlined"
              required
              value={hostUrl}
              onChange={(e) => setHostUrl(e.target.value)}
              helperText="White-label host this branding applies to"
              sx={{ maxWidth: 360 }}
            />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Logo
              </Typography>
              {logoPreview && (
                <Box
                  component="img"
                  src={logoPreview}
                  alt="logo preview"
                  sx={{
                    maxHeight: 80,
                    maxWidth: 240,
                    objectFit: "contain",
                    display: "block",
                    mb: 1,
                    border: "1px solid #eee",
                    p: 1,
                  }}
                />
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <input
                  ref={logoInputRef}
                  type="file"
                  onChange={(e) => handleFileChange(e, "logo")}
                />
                {newLogo && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleClear("logo")}
                    sx={{ textTransform: "none" }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Favicon
              </Typography>
              {faviconPreview && (
                <Box
                  component="img"
                  src={faviconPreview}
                  alt="favicon preview"
                  sx={{
                    maxHeight: 48,
                    maxWidth: 48,
                    objectFit: "contain",
                    display: "block",
                    mb: 1,
                    border: "1px solid #eee",
                    p: 1,
                  }}
                />
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <input
                  ref={faviconInputRef}
                  type="file"
                  onChange={(e) => handleFileChange(e, "favicon")}
                />
                {newFavicon && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleClear("favicon")}
                    sx={{ textTransform: "none" }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Box>

            <Box>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSave}
                disabled={saveLoader}
                sx={{ textTransform: "none" }}
              >
                Save Branding{" "}
                {saveLoader && <CircularProgress size={"1rem"} sx={{ ml: 1 }} />}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
